import React, { useEffect } from "react";
import { useLocation } from "react-router";
import { authorized, buildFeatureFlagChecker } from "common/authorization";
import { FEATURE_FLAG_NAME } from "common/constants";
import { accountSupportsElevationCertificates } from "common/utils/documentTypes";
import { useSecurityConfiguration } from "../../hooks/useSecurityConfiguration";
import { buildLoginURLWithPrevLocation } from "../../utils/params";
import FullPageLoadingScreen from "../Common/FullPageLoadingScreen";
import { MainRouteProps } from "../types";
import { AuthContextType, CurrentAdmin } from "./types";
import { trackEntity } from "./utils";

export const AuthContext = React.createContext<AuthContextType>({
  account: null,
  user: null,
  admin: null,
  publicUser: null,
  loadingAuthorizedEntity: false,
  isPublic: true,
  authorized: () => false,
  setAdmin: (_admin: CurrentAdmin) => {},
  isFeatureEnabled: (_: FEATURE_FLAG_NAME) => false,
  supportsElevationCertificates: false,
  refetchAuthorizedEntity: () => {},
});

export const AuthContextProvider = ({
  children,
  user,
  admin,
  loadingAuthorizedEntity,
  refetchAuthorizedEntity,
}: React.PropsWithChildren<MainRouteProps>) => {
  const location = useLocation();
  const [currentAdmin, setCurrentAdmin] = React.useState<Maybe<CurrentAdmin>>(
    admin ?? null,
  );

  const entity = user || admin;
  const { securityConfiguration } = useSecurityConfiguration({
    entity,
  });

  useEffect(() => {
    if (entity) {
      trackEntity(entity);
    }
  }, [entity]);

  if (!entity) {
    window.location.href = buildLoginURLWithPrevLocation(location);
    return <FullPageLoadingScreen />;
  }

  const account = entity.account;
  const supportsElevationCertificates =
    accountSupportsElevationCertificates(account);
  const featureFlags = account?.featureFlags || [];

  return (
    <AuthContext.Provider
      value={{
        setAdmin: setCurrentAdmin,
        admin: currentAdmin ?? null,
        user: user ?? null,
        publicUser: null,
        loadingAuthorizedEntity: loadingAuthorizedEntity ?? false,
        refetchAuthorizedEntity: refetchAuthorizedEntity ?? (() => {}),
        account: account ? { ...account, securityConfiguration } : null,
        isPublic: false,
        authorized: authDetails => authorized({ entity, ...authDetails }),
        isFeatureEnabled: buildFeatureFlagChecker(featureFlags),
        supportsElevationCertificates,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
